import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Coins,
  Filter,
  Globe,
  Info,
  Layers,
  Mail,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import {
  fetchSimplyloanLtvCohorts,
  getInitialLtvResponse,
  LtvAttributionMode,
  LtvCohortRow,
  LtvCountry,
  LtvEmailStep,
  LtvSummaryStats,
} from '../services/simplyloanLtvApi';

const dkkFmt = new Intl.NumberFormat('da-DK', {
  style: 'currency',
  currency: 'DKK',
  maximumFractionDigits: 0,
});
const dkkDecFmt = new Intl.NumberFormat('da-DK', {
  style: 'currency',
  currency: 'DKK',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const intFmt = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });

const fmtDkk = (v: number | null | undefined, decimals = false) => {
  if (v == null || Number.isNaN(v)) return '–';
  return decimals ? dkkDecFmt.format(v) : dkkFmt.format(v);
};

const fmtInt = (v: number | null | undefined) => {
  if (v == null || Number.isNaN(v)) return '–';
  return intFmt.format(v);
};

const fmtPct = (v: number | null | undefined) => {
  if (v == null || Number.isNaN(v)) return '–';
  return `${v.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const fmtRoasX = (pct: number | null | undefined) => {
  if (pct == null || Number.isNaN(pct)) return '–';
  return `${(pct / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
};

function roasBadgeClass(pct: number | null | undefined): string {
  if (pct == null) return 'bg-slate-100 text-slate-600 border-slate-200';
  if (pct >= 120) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (pct >= 100) return 'bg-teal-50 text-teal-700 border-teal-200';
  if (pct >= 70) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
}

const COUNTRY_OPTIONS: { id: LtvCountry; label: string; flag: string; sub: string }[] = [
  { id: 'all', label: 'Begge lande (NO + SE)', flag: '🌍', sub: 'Samlet 24 mdr. portefølje' },
  { id: 'se', label: 'Sverige (SimplyLoan.se)', flag: '🇸🇪', sub: 'Meta + Microsoft Ads + Brevo' },
  { id: 'no', label: 'Norge (SimplyLoan.no)', flag: '🇳🇴', sub: 'Meta Ads + Brevo / SMS' },
];

const ATTRIBUTION_MODES: {
  id: LtvAttributionMode;
  title: string;
  badge: string;
  desc: string;
}[] = [
  {
    id: 'strict_lead',
    title: 'Kun 1:1 LeadID Match',
    badge: 'Strikt 1:1',
    desc: 'Kun konverteringer med direkte LeadID / WCT-session i MySQL (ekskl. anonyme tilbudsvæg-klik & email-revisits)',
  },
  {
    id: 'standard',
    title: 'Standard Kohorte',
    badge: 'Standard',
    desc: 'Alle website/annonce-salg i annoncemåneden + direkte & revisit-matchede email-salg',
  },
  {
    id: 'blended',
    title: 'Inkl. Fordelt Skjult LTV',
    badge: 'Fuld LTV',
    desc: 'Fordeler unmatchet email (s2="email", fbem, brem, slsebrlist) + 0-spend return-web proportionalt på aktive betalte kohorter',
  },
];

function getSummaryView(s: LtvSummaryStats, mode: LtvAttributionMode) {
  if (mode === 'strict_lead') {
    const immRev = s.immediate_direct_lead_dkk ?? s.immediate_revenue_dkk;
    const immRoasPct = s.immediate_direct_lead_roas_pct ?? s.immediate_roas_pct;
    const emailRev = s.strict_email_revenue_dkk ?? s.total_email_revenue_dkk;
    const emailRoasLiftPct = s.strict_email_roas_lift_pct ?? s.email_roas_lift_pct;
    const totalLtvRev = s.strict_lead_ltv_revenue_dkk ?? immRev + emailRev;
    const totalLtvRoasPct = s.strict_lead_ltv_roas_pct ?? s.total_ltv_roas_pct;
    const ltvPerLead = s.strict_lead_ltv_per_lead_dkk ?? s.ltv_per_lead_dkk;
    const immPerLead = s.strict_lead_immediate_per_lead_dkk ?? s.immediate_per_lead_dkk;
    const emailPerLead = s.strict_lead_email_per_lead_dkk ?? s.email_per_lead_dkk;
    const netProfit = s.strict_lead_net_profit_dkk ?? totalLtvRev - s.spend_dkk;
    const roasMult = s.strict_lead_roas_multiplier ?? s.roas_multiplier;
    return {
      immRev,
      immRoasPct,
      m0EmailRev: s.m0_email_revenue_dkk,
      afterEmailRev: Math.max(0, emailRev - s.m0_email_revenue_dkk),
      emailRev,
      emailRoasLiftPct,
      allocatedHiddenRev: 0,
      totalLtvRev,
      totalLtvRoasPct,
      ltvPerLead,
      immPerLead,
      emailPerLead,
      netProfit,
      roasMult,
    };
  }
  if (mode === 'blended') {
    const immRev = s.immediate_revenue_dkk;
    const immRoasPct = s.immediate_roas_pct;
    const allocatedHiddenRev = s.allocated_hidden_ltv_dkk ?? 0;
    const emailRev = s.blended_email_revenue_dkk ?? s.total_email_revenue_dkk + allocatedHiddenRev;
    const emailRoasLiftPct = s.blended_email_roas_lift_pct ?? s.email_roas_lift_pct;
    const totalLtvRev = s.blended_ltv_revenue_dkk ?? s.total_ltv_revenue_dkk;
    const totalLtvRoasPct = s.blended_ltv_roas_pct ?? s.total_ltv_roas_pct;
    const ltvPerLead = s.blended_ltv_per_lead_dkk ?? s.ltv_per_lead_dkk;
    const immPerLead = s.immediate_per_lead_dkk;
    const emailPerLead = s.blended_email_per_lead_dkk ?? s.email_per_lead_dkk;
    const netProfit = s.blended_net_profit_dkk ?? s.net_profit_dkk;
    const roasMult = s.blended_roas_multiplier ?? s.roas_multiplier;
    return {
      immRev,
      immRoasPct,
      m0EmailRev: s.m0_email_revenue_dkk,
      afterEmailRev: Math.max(0, emailRev - s.m0_email_revenue_dkk),
      emailRev,
      emailRoasLiftPct,
      allocatedHiddenRev,
      totalLtvRev,
      totalLtvRoasPct,
      ltvPerLead,
      immPerLead,
      emailPerLead,
      netProfit,
      roasMult,
    };
  }
  return {
    immRev: s.immediate_revenue_dkk,
    immRoasPct: s.immediate_roas_pct,
    m0EmailRev: s.m0_email_revenue_dkk,
    afterEmailRev: s.subsequent_email_revenue_dkk,
    emailRev: s.total_email_revenue_dkk,
    emailRoasLiftPct: s.email_roas_lift_pct,
    allocatedHiddenRev: 0,
    totalLtvRev: s.total_ltv_revenue_dkk,
    totalLtvRoasPct: s.total_ltv_roas_pct,
    ltvPerLead: s.ltv_per_lead_dkk,
    immPerLead: s.immediate_per_lead_dkk,
    emailPerLead: s.email_per_lead_dkk,
    netProfit: s.net_profit_dkk,
    roasMult: s.roas_multiplier,
  };
}

function getCohortView(c: LtvCohortRow, mode: LtvAttributionMode) {
  if (mode === 'strict_lead') {
    const immRev = c.immediate_direct_lead_dkk ?? c.immediate_revenue_dkk;
    const immRoasPct = c.immediate_direct_lead_roas_pct ?? c.immediate_roas_pct;
    const emailRev = c.strict_email_revenue_dkk ?? c.total_email_revenue_dkk;
    const emailRoasLiftPct =
      c.spend_dkk > 0 ? Math.round((emailRev / c.spend_dkk) * 1000) / 10 : null;
    const totalLtvRev = c.strict_lead_ltv_revenue_dkk ?? immRev + emailRev;
    const totalLtvRoasPct = c.strict_lead_ltv_roas_pct ?? c.total_ltv_roas_pct;
    const ltvPerLead = c.strict_lead_ltv_per_lead_dkk ?? c.ltv_per_lead_dkk;
    const netProfit = c.strict_lead_net_profit_dkk ?? totalLtvRev - c.spend_dkk;
    const m0Step = c.email_timeline.find((s) => s.offset === 0);
    const m0EmailRev = m0Step?.strict_email_revenue_dkk ?? c.m0_email_revenue_dkk;
    return {
      immRev,
      immRoasPct,
      m0EmailRev,
      afterEmailRev: Math.max(0, emailRev - m0EmailRev),
      emailRev,
      emailRoasLiftPct,
      allocatedHiddenRev: 0,
      totalLtvRev,
      totalLtvRoasPct,
      ltvPerLead,
      netProfit,
      breakevenOffset: c.breakeven_offset,
    };
  }
  if (mode === 'blended') {
    const immRev = c.immediate_revenue_dkk;
    const immRoasPct = c.immediate_roas_pct;
    const allocatedHiddenRev = c.allocated_hidden_ltv_dkk ?? 0;
    const emailRev = c.blended_email_revenue_dkk ?? c.total_email_revenue_dkk + allocatedHiddenRev;
    const emailRoasLiftPct = c.blended_email_roas_lift_pct ?? c.email_roas_lift_pct;
    const totalLtvRev = c.blended_ltv_revenue_dkk ?? c.total_ltv_revenue_dkk;
    const totalLtvRoasPct = c.blended_ltv_roas_pct ?? c.total_ltv_roas_pct;
    const ltvPerLead = c.blended_ltv_per_lead_dkk ?? c.ltv_per_lead_dkk;
    const netProfit = c.blended_net_profit_dkk ?? c.net_profit_dkk;
    const m0EmailRev = c.m0_blended_email_revenue_dkk ?? c.m0_email_revenue_dkk;
    const afterEmailRev = c.subsequent_blended_email_revenue_dkk ?? c.subsequent_email_revenue_dkk;
    return {
      immRev,
      immRoasPct,
      m0EmailRev,
      afterEmailRev,
      emailRev,
      emailRoasLiftPct,
      allocatedHiddenRev,
      totalLtvRev,
      totalLtvRoasPct,
      ltvPerLead,
      netProfit,
      breakevenOffset: c.blended_breakeven_offset ?? c.breakeven_offset,
    };
  }
  return {
    immRev: c.immediate_revenue_dkk,
    immRoasPct: c.immediate_roas_pct,
    m0EmailRev: c.m0_email_revenue_dkk,
    afterEmailRev: c.subsequent_email_revenue_dkk,
    emailRev: c.total_email_revenue_dkk,
    emailRoasLiftPct: c.email_roas_lift_pct,
    allocatedHiddenRev: 0,
    totalLtvRev: c.total_ltv_revenue_dkk,
    totalLtvRoasPct: c.total_ltv_roas_pct,
    ltvPerLead: c.ltv_per_lead_dkk,
    netProfit: c.net_profit_dkk,
    breakevenOffset: c.breakeven_offset,
  };
}

function getStepView(step: LtvEmailStep, mode: LtvAttributionMode, cohort: LtvCohortRow) {
  if (mode === 'strict_lead') {
    const stepRev = step.strict_email_revenue_dkk ?? step.email_revenue_dkk;
    const cumEmailRev = step.strict_cumulative_email_revenue_dkk ?? step.cumulative_email_revenue_dkk;
    const cumTotalRev = step.strict_cumulative_total_revenue_dkk ?? step.cumulative_total_revenue_dkk;
    const cumRoasPct = step.strict_cumulative_roas_pct ?? step.cumulative_roas_pct;
    const incRoasPct =
      cohort.spend_dkk > 0 ? Math.round((stepRev / cohort.spend_dkk) * 1000) / 10 : null;
    const ltvPerLead =
      cohort.unique_leads > 0 ? Math.round((cumTotalRev / cohort.unique_leads) * 100) / 100 : 0;
    return {
      stepRev,
      allocEmailRev: 0,
      allocWebRev: 0,
      cumEmailRev,
      cumTotalRev,
      cumRoasPct,
      incRoasPct,
      ltvPerLead,
    };
  }
  if (mode === 'blended') {
    const stepRev = step.blended_step_revenue_dkk ?? step.email_revenue_dkk;
    const allocEmailRev = step.allocated_email_revenue_dkk ?? 0;
    const allocWebRev = step.allocated_return_web_dkk ?? 0;
    const cumEmailRev = step.blended_cumulative_email_revenue_dkk ?? step.cumulative_email_revenue_dkk;
    const cumTotalRev = step.blended_cumulative_total_revenue_dkk ?? step.cumulative_total_revenue_dkk;
    const cumRoasPct = step.blended_cumulative_roas_pct ?? step.cumulative_roas_pct;
    const incRoasPct = step.blended_incremental_roas_pct ?? step.incremental_roas_pct;
    const ltvPerLead = step.blended_cumulative_ltv_per_lead_dkk ?? step.cumulative_ltv_per_lead_dkk;
    return {
      stepRev,
      allocEmailRev,
      allocWebRev,
      cumEmailRev,
      cumTotalRev,
      cumRoasPct,
      incRoasPct,
      ltvPerLead,
    };
  }
  return {
    stepRev: step.email_revenue_dkk,
    allocEmailRev: 0,
    allocWebRev: 0,
    cumEmailRev: step.cumulative_email_revenue_dkk,
    cumTotalRev: step.cumulative_total_revenue_dkk,
    cumRoasPct: step.cumulative_roas_pct,
    incRoasPct: step.incremental_roas_pct,
    ltvPerLead: step.cumulative_ltv_per_lead_dkk,
  };
}

const SimplyloanLtvDashboard: React.FC = () => {
  const [country, setCountry] = useState<LtvCountry>('se');
  const [selectedMonth, setSelectedMonth] = useState<string>('2026-03');
  const [onlyPaidCohorts, setOnlyPaidCohorts] = useState<boolean>(true);
  const [attributionMode, setAttributionMode] = useState<LtvAttributionMode>('standard');
  const [showUnmatchedPanel, setShowUnmatchedPanel] = useState<boolean>(true);
  const [forceRefreshToken, setForceRefreshToken] = useState<number>(0);

  const initialData = useMemo(() => getInitialLtvResponse(country), [country]);

  const { data: apiResp, isFetching, refetch } = useQuery({
    queryKey: ['simplyloan-ltv-cohorts', country, forceRefreshToken],
    queryFn: ({ signal }) =>
      fetchSimplyloanLtvCohorts({
        country,
        refresh: forceRefreshToken > 0,
        signal,
      }),
    initialData,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });

  const payload = apiResp?.data || initialData.data;
  const comparison = apiResp?.country_comparison || initialData.country_comparison;

  const filteredCohorts = useMemo(() => {
    const list = (payload?.cohorts || []).filter((c) => c.is_valid);
    const subset = onlyPaidCohorts ? list.filter((c) => c.has_ad_spend) : list;
    return [...subset].sort((a, b) => b.month.localeCompare(a.month));
  }, [payload, onlyPaidCohorts]);

  // Ensure selectedMonth is valid in current filtered list
  const activeCohort: LtvCohortRow | null = useMemo(() => {
    if (!filteredCohorts.length) return null;
    const exact = filteredCohorts.find((c) => c.month === selectedMonth);
    if (exact) return exact;
    // Pick most recent cohort with both spend and email maturity if available
    const withEmail = filteredCohorts.find((c) => c.has_ad_spend && c.total_email_revenue_dkk > 0);
    return withEmail || filteredCohorts[0];
  }, [filteredCohorts, selectedMonth]);

  const activeSummary: LtvSummaryStats = onlyPaidCohorts
    ? payload.summary_paid_cohorts
    : payload.summary_all_valid;

  const summaryView = useMemo(
    () => getSummaryView(activeSummary, attributionMode),
    [activeSummary, attributionMode]
  );

  const activeCohortView = useMemo(
    () => (activeCohort ? getCohortView(activeCohort, attributionMode) : null),
    [activeCohort, attributionMode]
  );

  const unmatchedAnalysis = payload.unmatched_traffic_analysis;

  const handleCountryChange = (next: LtvCountry) => {
    setCountry(next);
    const nextData = getInitialLtvResponse(next).data;
    const paid = nextData.cohorts.filter((c) => c.has_ad_spend);
    if (paid.some((c) => c.month === selectedMonth)) {
      return;
    }
    if (next === 'no') {
      setSelectedMonth('2025-05');
    } else {
      setSelectedMonth('2026-03');
    }
  };

  // Helper to get email revenue at specific offset for matrix table
  const getEmailAtOffset = (cohort: LtvCohortRow, offset: number): number | null => {
    const step = cohort.email_timeline.find((s) => s.offset === offset);
    if (!step) return null;
    return getStepView(step, attributionMode, cohort).stepRev;
  };

  const getEmailFromOffsetOnwards = (cohort: LtvCohortRow, minOffset: number): number | null => {
    const steps = cohort.email_timeline.filter((s) => s.offset >= minOffset);
    if (!steps.length) return null;
    return steps.reduce((acc, s) => acc + getStepView(s, attributionMode, cohort).stepRev, 0);
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-50/70 pb-16">
        {/* Top Hero & Navigation Bar */}
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                  <Link to="/simplyloan" className="hover:text-slate-900">
                    SimplyLoan
                  </Link>
                  <ChevronRight size={13} />
                  <Link to="/simplyloan/meta" className="hover:text-slate-900">
                    Meta Ads
                  </Link>
                  <ChevronRight size={13} />
                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 font-semibold text-emerald-800">
                    24 Mdr. Kohorte & Email LTV
                  </span>
                </div>
                <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  SimplyLoan Kohorte & Email LTV Dashboard (Norge + Sverige)
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  Leads grupperet pr. erhvervelsesmåned ({payload.start_month} til {payload.end_month}) — fra{' '}
                  <span className="font-semibold text-slate-800">Immediate ROAS på annoncen (Måned 0)</span> til{' '}
                  <span className="font-semibold text-emerald-700">
                    efterfølgende Email LTV måned for måned (M+0, M+1, M+2...)
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <Link
                  to="/simplyloan/meta"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Meta Ads Dashboard
                </Link>
                <Link
                  to="/simplyloan/email"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Email Hub
                </Link>
                <Link
                  to="/simplyloan/redirect-tracking"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Live Tracking
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setForceRefreshToken((n) => n + 1);
                    refetch();
                  }}
                  disabled={isFetching}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F3F34] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#165446] disabled:opacity-60"
                >
                  <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                  {isFetching ? 'Opdaterer...' : 'Opdater data'}
                </button>
              </div>
            </div>

            {/* Primary Controls: Country + Cohort Month + Paid Filter */}
            <div className="mt-5 grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4 lg:grid-cols-12">
              {/* 1. Country Selector */}
              <div className="lg:col-span-5">
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                  <Globe size={13} className="text-emerald-700" />
                  1. Vælg Land
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {COUNTRY_OPTIONS.map((opt) => {
                    const active = country === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleCountryChange(opt.id)}
                        className={`flex flex-col items-start rounded-lg border px-3 py-2 text-left transition ${
                          active
                            ? 'border-[#0F3F34] bg-[#0F3F34] text-white shadow-sm'
                            : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 text-xs font-bold">
                          <span>{opt.flag}</span>
                          <span className="truncate">
                            {opt.id === 'all' ? 'Begge (NO+SE)' : opt.id === 'se' ? 'Sverige (SE)' : 'Norge (NO)'}
                          </span>
                        </div>
                        <span className={`mt-0.5 text-[10px] ${active ? 'text-emerald-100' : 'text-slate-500'}`}>
                          {fmtDkk(comparison[opt.id]?.spend_dkk)} spend
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Month Cohort Selector */}
              <div className="lg:col-span-4">
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                  <Calendar size={13} className="text-emerald-700" />
                  2. Vælg Kohorte Måned ({filteredCohorts.length} gyldige måneder)
                </label>
                <select
                  value={activeCohort?.month || ''}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 shadow-sm focus:border-[#0F3F34] focus:outline-none focus:ring-2 focus:ring-[#0F3F34]/20"
                >
                  {filteredCohorts.map((c) => {
                    const cv = getCohortView(c, attributionMode);
                    return (
                      <option key={c.month} value={c.month}>
                        {c.month_label} — {fmtInt(c.unique_leads)} leads | Spend: {fmtDkk(c.spend_dkk)} | Imm. ROAS:{' '}
                        {cv.immRoasPct != null ? `${cv.immRoasPct}%` : '–'} → LTV ROAS:{' '}
                        {cv.totalLtvRoasPct != null ? `${cv.totalLtvRoasPct}%` : '–'}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* 3. Cohort Mode Filter */}
              <div className="lg:col-span-3">
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                  <Filter size={13} className="text-emerald-700" />
                  3. Månedsfilter
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setOnlyPaidCohorts(true)}
                    className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                      onlyPaidCohorts
                        ? 'border-emerald-700 bg-emerald-50 text-emerald-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Kun Annonce-måneder
                    <span className="block text-[10px] font-normal text-slate-500">
                      ({payload.summary_paid_cohorts.cohorts_count} mdr. med spend)
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnlyPaidCohorts(false)}
                    className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                      !onlyPaidCohorts
                        ? 'border-emerald-700 bg-emerald-50 text-emerald-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Alle Gyldige Måneder
                    <span className="block text-[10px] font-normal text-slate-500">
                      ({payload.summary_all_valid.cohorts_count} mdr. inkl. organisk)
                    </span>
                  </button>
                </div>
              </div>

              {/* 4. Attribution Mode Selector (Direct 1:1 LeadID vs Standard vs Blended Full LTV) */}
              <div className="border-t border-slate-200/80 pt-3 lg:col-span-12">
                <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-emerald-700" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      4. Håndtering af trafik uden direkte LeadID (Tilbudsvæg, s2="email" & Return Web):
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:w-[760px]">
                    {ATTRIBUTION_MODES.map((m) => {
                      const active = attributionMode === m.id;
                      const previewView = getSummaryView(activeSummary, m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setAttributionMode(m.id)}
                          title={m.desc}
                          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left transition ${
                            active
                              ? 'border-[#0F3F34] bg-[#0F3F34] text-white shadow-sm'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <div>
                            <div className="text-xs font-bold">{m.title}</div>
                            <div className={`text-[10px] ${active ? 'text-emerald-100' : 'text-slate-500'}`}>
                              {fmtDkk(previewView.totalLtvRev)} LTV
                            </div>
                          </div>
                          <span
                            className={`rounded-md px-2 py-0.5 text-xs font-extrabold ${
                              active ? 'bg-emerald-400/20 text-emerald-200' : 'bg-slate-100 text-slate-800'
                            }`}
                          >
                            {fmtPct(previewView.totalLtvRoasPct)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1600px] space-y-6 px-4 pt-6 sm:px-6 lg:px-8">
          {/* ─── SECTION 1: 24-Month Portfolio Summary (Immediate vs. Email LTV) ─── */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {/* Tile 1: Total Leads & Spend */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  1. Samlede Leads & Spend
                </span>
                <Users size={16} className="text-slate-400" />
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900">{fmtInt(activeSummary.unique_leads)} leads</p>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                <span>Annonce-spend:</span>
                <span className="font-semibold text-slate-900">{fmtDkk(activeSummary.spend_dkk)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>Gns. CPL (pr. lead):</span>
                <span className="font-semibold text-slate-800">{fmtDkk(activeSummary.cpl_dkk, true)}</span>
              </div>
            </div>

            {/* Tile 2: Immediate ROAS on Ad */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  2. Immediate ROAS (Måned 0)
                </span>
                <Coins size={16} className="text-amber-500" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <p className="text-2xl font-bold text-slate-900">{fmtPct(summaryView.immRoasPct)}</p>
                <span className="text-xs font-semibold text-slate-500">({fmtRoasX(summaryView.immRoasPct)})</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                <span>Immediate omsætning:</span>
                <span className="font-semibold text-slate-900">{fmtDkk(summaryView.immRev)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {attributionMode === 'strict_lead' ? 'Kun 1:1 LeadID match:' : 'Heraf 1:1 LeadID / Tilbudsvæg:'}
                </span>
                <span className="font-semibold text-slate-800">
                  {attributionMode === 'strict_lead'
                    ? fmtDkk(summaryView.immPerLead, true)
                    : `${fmtDkk(activeSummary.immediate_direct_lead_dkk)} / ${fmtDkk(
                        activeSummary.immediate_unmatched_total_dkk
                      )}`}
                </span>
              </div>
            </div>

            {/* Tile 3: Email Revenue in Month 0 + All Months After */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800">
                  3. Email & Efterfølgende LTV
                </span>
                <Mail size={16} className="text-emerald-600" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <p className="text-2xl font-bold text-emerald-800">+{fmtDkk(summaryView.emailRev)}</p>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">
                  +{fmtPct(summaryView.emailRoasLiftPct)} ROAS
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-emerald-900/80">
                <span>M+0 (samme md.) / M+1+ (efter):</span>
                <span className="font-semibold">
                  {fmtDkk(summaryView.m0EmailRev)} / {fmtDkk(summaryView.afterEmailRev)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-emerald-900/70">
                <span>
                  {attributionMode === 'blended' && summaryView.allocatedHiddenRev > 0
                    ? 'Heraf fordelt skjult LTV:'
                    : 'Email LTV pr. lead:'}
                </span>
                <span className="font-semibold">
                  {attributionMode === 'blended' && summaryView.allocatedHiddenRev > 0
                    ? `+${fmtDkk(summaryView.allocatedHiddenRev)} (+${fmtDkk(summaryView.emailPerLead, true)}/lead)`
                    : `+${fmtDkk(summaryView.emailPerLead, true)} (${fmtInt(
                        activeSummary.total_email_conversions
                      )} salg)`}
                </span>
              </div>
            </div>

            {/* Tile 4: Total LTV ROAS */}
            <div className="rounded-xl border-2 border-[#0F3F34] bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-[#0F3F34]">
                  4. Samlet LTV ROAS ({ATTRIBUTION_MODES.find((m) => m.id === attributionMode)?.badge})
                </span>
                <TrendingUp size={16} className="text-[#0F3F34]" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <p className="text-2xl font-extrabold text-slate-900">{fmtPct(summaryView.totalLtvRoasPct)}</p>
                {summaryView.roasMult && (
                  <span className="rounded-full bg-[#0F3F34]/10 px-2 py-0.5 text-xs font-bold text-[#0F3F34]">
                    {summaryView.roasMult.toLocaleString('da-DK')}× Immediate
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                <span>Total LTV omsætning:</span>
                <span className="font-bold text-slate-900">{fmtDkk(summaryView.totalLtvRev)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>Netto P&L (LTV - Spend):</span>
                <span
                  className={`font-bold ${
                    summaryView.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'
                  }`}
                >
                  {summaryView.netProfit >= 0 ? '+' : ''}
                  {fmtDkk(summaryView.netProfit)}
                </span>
              </div>
            </div>

            {/* Tile 5: LTV Per Lead vs CPL */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  5. LTV pr. Lead vs. CPL
                </span>
                <Sparkles size={16} className="text-orange-500" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <p className="text-2xl font-bold text-slate-900">{fmtDkk(summaryView.ltvPerLead, true)}</p>
                <span className="text-xs text-slate-500">LTV / lead</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                <span>Annonce-CPL pr. lead:</span>
                <span className="font-semibold text-slate-800">{fmtDkk(activeSummary.cpl_dkk, true)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>Netto margin pr. lead:</span>
                {activeSummary.cpl_dkk != null ? (
                  <span
                    className={`font-bold ${
                      summaryView.ltvPerLead - activeSummary.cpl_dkk >= 0
                        ? 'text-emerald-700'
                        : 'text-rose-600'
                    }`}
                  >
                    {summaryView.ltvPerLead - activeSummary.cpl_dkk >= 0 ? '+' : ''}
                    {fmtDkk(summaryView.ltvPerLead - activeSummary.cpl_dkk, true)}
                  </span>
                ) : (
                  <span>–</span>
                )}
              </div>
            </div>
          </div>

          {/* ─── SECTION 1B: Unmatched Traffic, Hidden LTV & CoReg Breakdown ─── */}
          {unmatchedAnalysis && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-900">
                      Trafik & Attribuerings-analyse
                    </span>
                    <h2 className="text-base font-bold text-slate-900">
                      Hvad med den SimplyLoan-trafik og omsætning, der ikke har et direkte LeadID?
                    </h2>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-600">
                    Fuldt overblik over de 4 kategorier af trafik/konverteringer uden 1:1 LeadID — og hvordan de påvirker
                    din LTV ROAS, når du skifter mellem <strong>Kun 1:1 LeadID</strong>,{' '}
                    <strong>Standard Kohorte</strong> og <strong>Inkl. Fordelt Skjult LTV</strong>.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowUnmatchedPanel((v) => !v)}
                  className="self-start rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 sm:self-auto"
                >
                  {showUnmatchedPanel ? 'Skjul detaljer' : 'Vis de 4 trafik-kategorier'}
                </button>
              </div>

              {showUnmatchedPanel && (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {/* Bucket 1: Direct Wall Clickers & Offer #2/#3 */}
                  <div className="rounded-xl border border-amber-200/80 bg-amber-50/30 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-amber-900">
                      <span>1. Annonceklik uden formular</span>
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px]">
                        {fmtPct(unmatchedAnalysis.traffic_funnel.non_lead_click_share_pct)} af klik
                      </span>
                    </div>
                    <p className="mt-1.5 text-xl font-bold text-slate-900">
                      +{fmtDkk(unmatchedAnalysis.immediate_breakdown.unmatched_immediate_paid_months_dkk)}
                    </p>
                    <p className="text-[11px] text-slate-600">
                      Immediate website/annonce-salg uden direkte LeadID (med i <strong>Standard</strong> &{' '}
                      <strong>Fuld LTV</strong>)
                    </p>
                    <div className="mt-3 space-y-1 border-t border-amber-200/60 pt-2 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span>Betalte annonceklik:</span>
                        <span className="font-semibold">{fmtInt(unmatchedAnalysis.traffic_funnel.ad_clicks)} klik</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Klik uden formular ("Wall Clickers"):</span>
                        <span className="font-semibold">
                          {fmtInt(unmatchedAnalysis.traffic_funnel.non_lead_clicks)} (
                          {fmtPct(unmatchedAnalysis.traffic_funnel.non_lead_click_share_pct)})
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Annonce-tag uden LeadID:</span>
                        <span className="font-semibold">
                          {fmtDkk(unmatchedAnalysis.immediate_breakdown.ad_tag_no_lead_dkk)} (
                          {fmtInt(unmatchedAnalysis.immediate_breakdown.ad_tag_no_lead_conversions)} salg)
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tilbudsvæg (slseww/slnoww) u. LeadID:</span>
                        <span className="font-semibold">
                          {fmtDkk(unmatchedAnalysis.immediate_breakdown.wall_paid_month_no_lead_dkk)} (
                          {fmtInt(unmatchedAnalysis.immediate_breakdown.wall_paid_month_no_lead_conversions)} salg)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bucket 2: Email Revisits where Website Overwrote LeadID (s2="email") */}
                  <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/30 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-emerald-900">
                      <span>2. Email → Web uden LeadID</span>
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px]">
                        {fmtInt(unmatchedAnalysis.email_breakdown.unmatched_revisit_conversions)} salg
                      </span>
                    </div>
                    <p className="mt-1.5 text-xl font-bold text-emerald-800">
                      +{fmtDkk(unmatchedAnalysis.email_breakdown.unmatched_revisit_dkk)}
                    </p>
                    <p className="text-[11px] text-slate-600">
                      Nyhedsbreve der sendte brugeren på websitet, så <code>s2</code> blev overskrevet med{' '}
                      <code>"email"</code>
                    </p>
                    <div className="mt-3 space-y-1 border-t border-emerald-200/60 pt-2 text-xs text-slate-700">
                      {unmatchedAnalysis.email_breakdown.unmatched_sources.slice(0, 4).map((src) => (
                        <div key={src.source} className="flex justify-between">
                          <span className="font-mono text-[11px] text-slate-600">{src.source}</span>
                          <span className="font-semibold">
                            {fmtDkk(src.revenue_dkk)} ({src.conversions})
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-0.5 font-semibold text-emerald-800">
                        <span>Fordeles på kohorter i "Fuld LTV":</span>
                        <span>+{fmtDkk(activeSummary.allocated_unmatched_email_dkk)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Bucket 3: Zero-Spend Return Website Traffic */}
                  <div className="rounded-xl border border-sky-200/80 bg-sky-50/30 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-sky-900">
                      <span>3. Return Web i 0-Spend Mdr.</span>
                      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px]">
                        {fmtInt(unmatchedAnalysis.immediate_breakdown.zero_spend_return_web_conversions)} salg
                      </span>
                    </div>
                    <p className="mt-1.5 text-xl font-bold text-sky-900">
                      +{fmtDkk(unmatchedAnalysis.immediate_breakdown.zero_spend_return_web_dkk)}
                    </p>
                    <p className="text-[11px] text-slate-600">
                      Website-konverteringer (<code>slseww</code>/<code>slnoww</code>) i måneder hvor annoncer var 100%
                      slukkede
                    </p>
                    <div className="mt-3 space-y-1 border-t border-sky-200/60 pt-2 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span>Årsag:</span>
                        <span className="font-medium">Tidligere leads der vender tilbage</span>
                      </div>
                      <div className="flex justify-between">
                        <span>I "Standard Kohorte":</span>
                        <span className="font-medium">Ligger på 0-spend måneder</span>
                      </div>
                      <div className="flex justify-between font-semibold text-sky-900">
                        <span>I "Inkl. Fordelt Skjult LTV":</span>
                        <span>+{fmtDkk(activeSummary.allocated_return_web_dkk)} fordelt</span>
                      </div>
                    </div>
                  </div>

                  {/* Bucket 4: CoReg (c_<id>) & Pre-Window Leads */}
                  <div className="rounded-xl border border-purple-200/80 bg-purple-50/30 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-purple-900">
                      <span>4. CoReg (c_) & Før-Okt 2024</span>
                      <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px]">Holdt adskilt</span>
                    </div>
                    <p className="mt-1.5 text-xl font-bold text-purple-900">
                      +{fmtDkk(unmatchedAnalysis.email_breakdown.coreg_bonus_dkk)}
                    </p>
                    <p className="text-[11px] text-slate-600">
                      Ekstra email-omsætning på SimplyLoan-listerne fra CoReg-leads (<code>c_...</code>)
                    </p>
                    <div className="mt-3 space-y-1 border-t border-purple-200/60 pt-2 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span>CoReg (c_) på SimplyLoan email:</span>
                        <span className="font-semibold">
                          {fmtDkk(unmatchedAnalysis.email_breakdown.coreg_bonus_dkk)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Leads oprettet før okt 2024:</span>
                        <span className="font-semibold">
                          {fmtDkk(unmatchedAnalysis.email_breakdown.prior_window_leads_dkk)} (
                          {fmtInt(unmatchedAnalysis.email_breakdown.prior_window_leads_conversions)} salg)
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold text-purple-900">
                        <span>Samlet Email-kanal inkl. CoReg:</span>
                        <span>{fmtDkk(unmatchedAnalysis.email_breakdown.total_email_channel_dkk)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── SECTION 2: Selected Cohort Month Deep-Dive ("Se resultaterne måned for måned") ─── */}
          {activeCohort && activeCohortView && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              {/* Cohort Header */}
              <div className="flex flex-col justify-between gap-4 border-b border-slate-200 bg-slate-900 px-6 py-5 text-white lg:flex-row lg:items-center rounded-t-2xl">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-emerald-500/20 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-emerald-300">
                      Valgt Kohorte Deep-Dive
                    </span>
                    <span className="text-xs text-slate-300">
                      Sporingsperiode: {activeCohort.months_tracked} måneder ({activeCohort.month} → {payload.end_month})
                    </span>
                  </div>
                  <h2 className="mt-1 text-xl font-bold sm:text-2xl">
                    Kohorte: {activeCohort.month_label} —{' '}
                    {country === 'all' ? 'Norge + Sverige' : country === 'se' ? 'Sverige (SE)' : 'Norge (NO)'}
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-300">
                    Alle {fmtInt(activeCohort.unique_leads)} unikke leads genereret i {activeCohort.month_label} fulgt fra
                    dag 0 på annoncen og måned for måned på email efterfølgende.
                  </p>
                </div>

                {/* Quick Month Pills */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {filteredCohorts.slice(0, 10).map((c) => (
                    <button
                      key={c.month}
                      type="button"
                      onClick={() => setSelectedMonth(c.month)}
                      className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                        c.month === activeCohort.month
                          ? 'bg-emerald-500 text-slate-950 shadow-sm'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      {c.month_short}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* 4-Step Cohort Journey Cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  {/* Step A */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-slate-500">
                      <span>Trin 1: Lead-gruppe ({activeCohort.month_short})</span>
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-700">Kohorte</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold text-slate-900">
                      {fmtInt(activeCohort.unique_leads)} unikke leads
                    </p>
                    <div className="mt-2 space-y-1 text-xs text-slate-600">
                      <div className="flex justify-between">
                        <span>Annonce Spend ({activeCohort.month_short}):</span>
                        <span className="font-semibold text-slate-900">{fmtDkk(activeCohort.spend_dkk)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>CPL (Pris pr. lead):</span>
                        <span className="font-semibold text-slate-900">{fmtDkk(activeCohort.cpl_dkk, true)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Paid / Organiske leads:</span>
                        <span>
                          {fmtInt(activeCohort.paid_leads)} / {fmtInt(activeCohort.organic_leads)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Step B */}
                  <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-amber-900">
                      <span>Trin 2: Immediate ROAS (Annonce)</span>
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">Måned 0</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <p className="text-2xl font-bold text-slate-900">{fmtPct(activeCohortView.immRoasPct)}</p>
                      <span className="text-xs font-medium text-slate-600">
                        ({fmtDkk(activeCohortView.immRev)})
                      </span>
                    </div>
                    <div className="mt-2 space-y-1 text-xs text-slate-600">
                      <div className="flex justify-between">
                        <span>1:1 LeadID / Uden LeadID:</span>
                        <span className="font-semibold text-slate-900">
                          {fmtDkk(activeCohort.immediate_direct_lead_dkk ?? activeCohort.immediate_revenue_dkk)} /{' '}
                          {fmtDkk(activeCohort.immediate_unmatched_total_dkk ?? 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Immediate værdi pr. lead:</span>
                        <span className="font-semibold text-slate-900">
                          {fmtDkk(
                            activeCohort.unique_leads > 0
                              ? activeCohortView.immRev / activeCohort.unique_leads
                              : 0,
                            true
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Måned 0 inkl. M+0 Email:</span>
                        <span className="font-semibold text-slate-900">
                          {fmtPct(
                            activeCohort.spend_dkk > 0
                              ? ((activeCohortView.immRev + activeCohortView.m0EmailRev) / activeCohort.spend_dkk) * 100
                              : null
                          )}{' '}
                          ({fmtDkk(activeCohortView.immRev + activeCohortView.m0EmailRev)})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Step C */}
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-emerald-900">
                      <span>Trin 3: Email Måned for Måned</span>
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">
                        M+0 → M+{Math.max(0, activeCohort.months_tracked - 1)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <p className="text-2xl font-bold text-emerald-800">
                        +{fmtDkk(activeCohortView.emailRev)}
                      </p>
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-bold text-emerald-800">
                        +{fmtPct(activeCohortView.emailRoasLiftPct)} ROAS
                      </span>
                    </div>
                    <div className="mt-2 space-y-1 text-xs text-emerald-950/80">
                      <div className="flex justify-between">
                        <span>M+0 Email (samme måned):</span>
                        <span className="font-semibold">{fmtDkk(activeCohortView.m0EmailRev)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>M+1..M+{Math.max(1, activeCohort.months_tracked - 1)} ("måneder efter"):</span>
                        <span className="font-semibold">{fmtDkk(activeCohortView.afterEmailRev)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>
                          {attributionMode === 'blended' ? 'Heraf fordelt skjult LTV:' : 'Email LTV pr. lead:'}
                        </span>
                        <span className="font-semibold">
                          {attributionMode === 'blended'
                            ? `+${fmtDkk(activeCohortView.allocatedHiddenRev)}`
                            : `+${fmtDkk(
                                activeCohort.unique_leads > 0
                                  ? activeCohortView.emailRev / activeCohort.unique_leads
                                  : 0,
                                true
                              )}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Step D */}
                  <div className="rounded-xl border-2 border-[#0F3F34] bg-[#F4F8F6] p-4">
                    <div className="flex items-center justify-between text-xs font-bold uppercase text-[#0F3F34]">
                      <span>Trin 4: Samlet LTV Resultat</span>
                      <span className="rounded bg-[#0F3F34] px-1.5 py-0.5 text-[10px] text-white">Total LTV</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <p className="text-2xl font-extrabold text-slate-900">
                        {fmtPct(activeCohortView.totalLtvRoasPct)}
                      </p>
                      <span className="text-xs font-bold text-[#0F3F34]">
                        ({fmtDkk(activeCohortView.totalLtvRev)})
                      </span>
                    </div>
                    <div className="mt-2 space-y-1 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span>LTV pr. lead vs. CPL:</span>
                        <span className="font-bold text-slate-900">
                          {fmtDkk(activeCohortView.ltvPerLead, true)} vs. {fmtDkk(activeCohort.cpl_dkk, true)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Netto Resultat (Profit):</span>
                        <span
                          className={`font-bold ${
                            activeCohortView.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'
                          }`}
                        >
                          {activeCohortView.netProfit >= 0 ? '+' : ''}
                          {fmtDkk(activeCohortView.netProfit)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Break-even måned (≥100%):</span>
                        <span className="font-semibold text-slate-900">
                          {activeCohortView.breakevenOffset === 0
                            ? 'M+0 (Samme måned)'
                            : activeCohortView.breakevenOffset != null
                            ? `M+${activeCohortView.breakevenOffset} (${
                                activeCohort.email_timeline[activeCohortView.breakevenOffset]?.calendar_month_label || ''
                              })`
                            : 'Endnu under 100%'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Visual Cumulative LTV ROAS Progression Bar Chart */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                  <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Akkumuleret ROAS & Email LTV Udvikling Måned for Måned ({activeCohort.month_label})
                      </h3>
                      <p className="text-xs text-slate-500">
                        Viser hvordan kohorten starter på Immediate ROAS ({fmtPct(activeCohortView.immRoasPct)}) og
                        vokser måned for måned via efterfølgende email-konverteringer op til{' '}
                        <span className="font-semibold text-emerald-700">
                          {fmtPct(activeCohortView.totalLtvRoasPct)} LTV ROAS
                        </span>
                        .
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="inline-flex items-center gap-1.5 font-medium text-slate-600">
                        <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />
                        Immediate (Annonce)
                      </span>
                      <span className="inline-flex items-center gap-1.5 font-medium text-slate-600">
                        <span className="h-2.5 w-2.5 rounded-sm bg-emerald-600" />
                        Akkumuleret Email LTV
                      </span>
                    </div>
                  </div>

                  {/* Horizontal / Column Progression */}
                  <div className="mt-5 overflow-x-auto pb-2">
                    <div className="flex min-w-[680px] items-end gap-2.5 pt-6">
                      {/* Bar 0: Immediate Only */}
                      {(() => {
                        const maxRoas = Math.max(
                          120,
                          activeCohortView.totalLtvRoasPct || 100,
                          activeCohortView.immRoasPct || 0
                        );
                        const immPct = activeCohortView.immRoasPct || 0;
                        const immHeight = Math.min(180, Math.max(14, (immPct / maxRoas) * 170));

                        return (
                          <>
                            <div className="flex w-24 shrink-0 flex-col items-center">
                              <span className="mb-1 text-[11px] font-bold text-amber-800">{fmtPct(immPct)}</span>
                              <span className="mb-1 text-[10px] text-slate-500">
                                {fmtDkk(activeCohortView.immRev)}
                              </span>
                              <div className="flex h-[180px] w-full items-end justify-center rounded-t-lg bg-slate-100/80 px-2">
                                <div
                                  style={{ height: `${immHeight}px` }}
                                  className="w-full rounded-t-md bg-amber-500 transition-all"
                                  title={`Immediate ROAS: ${fmtPct(immPct)}`}
                                />
                              </div>
                              <div className="mt-1.5 text-center">
                                <p className="text-[11px] font-bold text-slate-800">Immediate</p>
                                <p className="text-[10px] text-slate-500">På annoncen</p>
                              </div>
                            </div>

                            <div className="flex h-[180px] items-center text-slate-300">
                              <ArrowRight size={16} />
                            </div>

                            {activeCohort.email_timeline.map((step) => {
                              const sv = getStepView(step, attributionMode, activeCohort);
                              const cumRoas = sv.cumRoasPct || 0;
                              const totalHeight = Math.min(180, Math.max(14, (cumRoas / maxRoas) * 170));
                              const immShare =
                                sv.cumTotalRev > 0
                                  ? activeCohortView.immRev / sv.cumTotalRev
                                  : 1;
                              const immBarH = Math.round(totalHeight * immShare);
                              const emailBarH = Math.max(0, totalHeight - immBarH);
                              const hasEmailThisMonth = sv.stepRev > 0;

                              return (
                                <div key={step.calendar_month} className="flex min-w-[68px] flex-1 flex-col items-center">
                                  <span
                                    className={`mb-0.5 text-[11px] font-bold ${
                                      cumRoas >= 100 ? 'text-emerald-700' : 'text-slate-700'
                                    }`}
                                  >
                                    {fmtPct(cumRoas)}
                                  </span>
                                  <span
                                    className={`mb-1 text-[10px] font-semibold ${
                                      hasEmailThisMonth ? 'text-emerald-600' : 'text-slate-400'
                                    }`}
                                  >
                                    {hasEmailThisMonth ? `+${fmtDkk(sv.stepRev)}` : '0 kr.'}
                                  </span>
                                  <div className="flex h-[180px] w-full flex-col justify-end rounded-t-lg bg-slate-100/80 px-1.5">
                                    {emailBarH > 0 && (
                                      <div
                                        style={{ height: `${emailBarH}px` }}
                                        className={`w-full rounded-t-md ${
                                          hasEmailThisMonth ? 'bg-emerald-600' : 'bg-emerald-500/75'
                                        }`}
                                      />
                                    )}
                                    <div
                                      style={{ height: `${immBarH}px` }}
                                      className={`w-full bg-amber-500/90 ${emailBarH === 0 ? 'rounded-t-md' : ''}`}
                                    />
                                  </div>
                                  <div className="mt-1.5 text-center">
                                    <p className="text-[11px] font-bold text-slate-800">{step.short_label}</p>
                                    <p className="text-[10px] text-slate-500">{step.calendar_month_label}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Detailed Month-by-Month Breakdown Table for Selected Cohort */}
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Måned-for-Måned Tabel for {activeCohort.month_label}-kohorten ({fmtInt(activeCohort.unique_leads)}{' '}
                        leads)
                      </h3>
                      <p className="text-xs text-slate-500">
                        Første række viser Immediate ROAS på annoncen. De efterfølgende rækker viser email-omsætning fra
                        præcis denne lead-gruppe måned for måned efterfølgende.
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-xs">
                      <thead className="bg-slate-100 text-slate-600">
                        <tr>
                          <th className="px-4 py-3 text-left font-bold uppercase tracking-wider">Periode / Trin</th>
                          <th className="px-4 py-3 text-left font-bold uppercase tracking-wider">Kalendermåned</th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">Konverteringer</th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">
                            Månedens Omsætning
                          </th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">
                            ROAS-bidrag i md.
                          </th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">
                            Akkumuleret Email
                          </th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">
                            Akkumuleret Total LTV
                          </th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">
                            Akkumuleret LTV ROAS
                          </th>
                          <th className="px-4 py-3 text-right font-bold uppercase tracking-wider">LTV pr. Lead</th>
                          <th className="px-4 py-3 text-left font-bold uppercase tracking-wider">
                            Konverterende Partnere
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {/* Row 0: Immediate Ad / Website Revenue */}
                        <tr className="bg-amber-50/40 font-medium">
                          <td className="px-4 py-3 font-bold text-amber-900">
                            Immediate (På annoncen)
                          </td>
                          <td className="px-4 py-3 text-slate-700">{activeCohort.month_label}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                            {fmtInt(
                              attributionMode === 'strict_lead'
                                ? activeCohort.immediate_direct_lead_conversions ?? activeCohort.immediate_conversions
                                : activeCohort.immediate_conversions
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-amber-900">
                            {fmtDkk(activeCohortView.immRev, true)}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-amber-900">
                            {fmtPct(activeCohortView.immRoasPct)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-400">0 kr.</td>
                          <td className="px-4 py-3 text-right font-bold text-slate-900">
                            {fmtDkk(activeCohortView.immRev, true)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${roasBadgeClass(
                                activeCohortView.immRoasPct
                              )}`}
                            >
                              {fmtPct(activeCohortView.immRoasPct)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-800">
                            {fmtDkk(
                              activeCohort.unique_leads > 0
                                ? activeCohortView.immRev / activeCohort.unique_leads
                                : 0,
                              true
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {activeCohort.immediate_partners.length > 0
                              ? activeCohort.immediate_partners
                                  .slice(0, 3)
                                  .map((p) => `${p.partner} (${fmtDkk(p.revenue_dkk)})`)
                                  .join(', ')
                              : '–'}
                          </td>
                        </tr>

                        {/* Subsequent Email Rows: M+0, M+1, M+2... */}
                        {activeCohort.email_timeline.map((step) => {
                          const sv = getStepView(step, attributionMode, activeCohort);
                          const hasRev = sv.stepRev > 0;
                          return (
                            <tr
                              key={step.calendar_month}
                              className={hasRev ? 'bg-white hover:bg-emerald-50/30' : 'bg-slate-50/40 text-slate-400'}
                            >
                              <td className="px-4 py-2.5 font-semibold text-slate-800">
                                <span className="inline-flex items-center gap-1.5">
                                  <Mail size={12} className={hasRev ? 'text-emerald-600' : 'text-slate-300'} />
                                  {step.offset === 0 ? 'Email M+0 (Samme md.)' : `Email ${step.short_label} (Md. efter)`}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">{step.calendar_month_full}</td>
                              <td className="px-4 py-2.5 text-right font-medium">
                                {step.email_conversions > 0 ? fmtInt(step.email_conversions) : hasRev ? 'Fordelt' : '–'}
                              </td>
                              <td
                                className={`px-4 py-2.5 text-right font-bold ${
                                  hasRev ? 'text-emerald-700' : 'text-slate-400'
                                }`}
                              >
                                {hasRev ? `+${fmtDkk(sv.stepRev, true)}` : '0 kr.'}
                                {attributionMode === 'blended' && (sv.allocEmailRev + sv.allocWebRev) > 0 && (
                                  <span className="block text-[10px] font-normal text-indigo-600">
                                    (heraf +{fmtDkk(sv.allocEmailRev + sv.allocWebRev)} fordelt skjult LTV)
                                  </span>
                                )}
                              </td>
                              <td
                                className={`px-4 py-2.5 text-right font-semibold ${
                                  hasRev ? 'text-emerald-700' : 'text-slate-400'
                                }`}
                              >
                                {hasRev && sv.incRoasPct != null
                                  ? `+${fmtPct(sv.incRoasPct)}`
                                  : '–'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-medium text-slate-700">
                                {fmtDkk(sv.cumEmailRev, true)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-bold text-slate-900">
                                {fmtDkk(sv.cumTotalRev, true)}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <span
                                  className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${roasBadgeClass(
                                    sv.cumRoasPct
                                  )}`}
                                >
                                  {fmtPct(sv.cumRoasPct)}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-right font-semibold text-slate-800">
                                {fmtDkk(sv.ltvPerLead, true)}
                              </td>
                              <td className="px-4 py-2.5 text-slate-600">
                                {step.partners.length > 0
                                  ? step.partners.map((p) => `${p.partner} (${fmtDkk(p.revenue_dkk)})`).join(', ')
                                  : hasRev && attributionMode === 'blended'
                                  ? 'Proportional andel af unmatchet email / return-web'
                                  : '–'}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Total Footer Row */}
                        <tr className="bg-slate-900 font-bold text-white">
                          <td className="px-4 py-3" colSpan={2}>
                            SAMLET LTV RESULTAT FOR {activeCohort.month_label.toUpperCase()} ({fmtInt(activeCohort.unique_leads)} LEADS | SPEND: {fmtDkk(activeCohort.spend_dkk)})
                          </td>
                          <td className="px-4 py-3 text-right">{fmtInt(activeCohort.total_ltv_conversions)}</td>
                          <td className="px-4 py-3 text-right text-emerald-300">
                            {fmtDkk(activeCohortView.totalLtvRev, true)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-300">
                            +{fmtPct(activeCohortView.emailRoasLiftPct)} fra email
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-300">
                            {fmtDkk(activeCohortView.emailRev, true)}
                          </td>
                          <td className="px-4 py-3 text-right text-white">
                            {fmtDkk(activeCohortView.totalLtvRev, true)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-300">
                            {fmtPct(activeCohortView.totalLtvRoasPct)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-300">
                            {fmtDkk(activeCohortView.ltvPerLead, true)}
                          </td>
                          <td className="px-4 py-3 text-right text-xs font-normal text-slate-300">
                            Netto: {activeCohortView.netProfit >= 0 ? '+' : ''}
                            {fmtDkk(activeCohortView.netProfit)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── SECTION 3: Full 24-Month Cohort Matrix Table (All Valid Months) ─── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col justify-between gap-3 pb-4 sm:flex-row sm:items-center">
              <div>
                <div className="flex items-center gap-2">
                  <Layers size={18} className="text-[#0F3F34]" />
                  <h2 className="text-lg font-bold text-slate-900">
                    24-Måneders Kohorte LTV Matrix — Alle Gyldige Måneder ({filteredCohorts.length} kohorter)
                  </h2>
                </div>
                <p className="mt-0.5 text-xs text-slate-600">
                  Klik på en vilkårlig måned i tabellen for at åbne hele dens måned-for-måned email-tidslinje ovenfor.
                  Kolonnerne <span className="font-semibold">M+0 Email</span> til <span className="font-semibold">M+6+</span>{' '}
                  viser hvor meget ekstra omsætning kohortens leads genererer på email i månederne efter annoncen.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-xs">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="sticky left-0 z-10 bg-slate-100 px-3 py-3 text-left font-bold uppercase">
                      Kohorte (Måned)
                    </th>
                    <th className="px-2.5 py-3 text-right font-bold uppercase">Leads</th>
                    <th className="px-2.5 py-3 text-right font-bold uppercase">Ad Spend</th>
                    <th className="px-2.5 py-3 text-right font-bold uppercase">CPL</th>
                    <th className="bg-amber-50/80 px-2.5 py-3 text-right font-bold uppercase text-amber-900">
                      Immediate Rev
                    </th>
                    <th className="bg-amber-50/80 px-2.5 py-3 text-right font-bold uppercase text-amber-900">
                      Immediate ROAS
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+0 Email
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+1
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+2
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+3
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+4
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+5
                    </th>
                    <th className="bg-emerald-50/60 px-2.5 py-3 text-right font-bold uppercase text-emerald-900">
                      M+6..24
                    </th>
                    <th className="bg-emerald-100/70 px-2.5 py-3 text-right font-bold uppercase text-emerald-950">
                      Total Email (+ROAS)
                    </th>
                    <th className="bg-slate-200/70 px-2.5 py-3 text-right font-bold uppercase text-slate-900">
                      Total LTV Rev
                    </th>
                    <th className="bg-slate-200/70 px-2.5 py-3 text-right font-bold uppercase text-slate-900">
                      LTV / Lead
                    </th>
                    <th className="bg-slate-200/70 px-3 py-3 text-right font-bold uppercase text-slate-900">
                      Total LTV ROAS
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {filteredCohorts.map((c) => {
                    const cv = getCohortView(c, attributionMode);
                    const isSelected = activeCohort?.month === c.month;
                    const m0 = getEmailAtOffset(c, 0);
                    const m1 = getEmailAtOffset(c, 1);
                    const m2 = getEmailAtOffset(c, 2);
                    const m3 = getEmailAtOffset(c, 3);
                    const m4 = getEmailAtOffset(c, 4);
                    const m5 = getEmailAtOffset(c, 5);
                    const m6Plus = getEmailFromOffsetOnwards(c, 6);

                    const renderEmailCell = (val: number | null) => {
                      if (val == null) return <span className="text-slate-300">–</span>;
                      if (val <= 0) return <span className="text-slate-300">0</span>;
                      return <span className="font-semibold text-emerald-700">+{fmtInt(val)}</span>;
                    };

                    return (
                      <tr
                        key={c.month}
                        onClick={() => setSelectedMonth(c.month)}
                        className={`cursor-pointer transition ${
                          isSelected
                            ? 'bg-emerald-50/70 ring-1 ring-inset ring-emerald-500'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="sticky left-0 z-10 bg-inherit px-3 py-2.5 font-bold text-slate-900">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`h-2 w-2 rounded-full ${
                                isSelected ? 'bg-emerald-600' : c.has_ad_spend ? 'bg-slate-400' : 'bg-slate-200'
                              }`}
                            />
                            <span>{c.month_short}</span>
                          </div>
                        </td>
                        <td className="px-2.5 py-2.5 text-right font-semibold text-slate-800">
                          {fmtInt(c.unique_leads)}
                        </td>
                        <td className="px-2.5 py-2.5 text-right font-medium text-slate-700">
                          {c.spend_dkk > 0 ? fmtDkk(c.spend_dkk) : '0 kr.'}
                        </td>
                        <td className="px-2.5 py-2.5 text-right text-slate-600">
                          {c.cpl_dkk != null ? `${fmtInt(c.cpl_dkk)} kr.` : '–'}
                        </td>
                        <td className="bg-amber-50/30 px-2.5 py-2.5 text-right font-semibold text-slate-900">
                          {fmtDkk(cv.immRev)}
                        </td>
                        <td className="bg-amber-50/30 px-2.5 py-2.5 text-right">
                          {cv.immRoasPct != null ? (
                            <span
                              className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-bold ${roasBadgeClass(
                                cv.immRoasPct
                              )}`}
                            >
                              {fmtPct(cv.immRoasPct)}
                            </span>
                          ) : (
                            <span className="text-slate-400">–</span>
                          )}
                        </td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m0)}</td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m1)}</td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m2)}</td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m3)}</td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m4)}</td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m5)}</td>
                        <td className="px-2.5 py-2.5 text-right">{renderEmailCell(m6Plus)}</td>
                        <td className="bg-emerald-50/40 px-2.5 py-2.5 text-right">
                          {cv.emailRev > 0 ? (
                            <div>
                              <span className="font-bold text-emerald-800">+{fmtDkk(cv.emailRev)}</span>
                              {cv.emailRoasLiftPct != null && (
                                <span className="ml-1 text-[10px] font-semibold text-emerald-600">
                                  (+{fmtPct(cv.emailRoasLiftPct)})
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">0 kr.</span>
                          )}
                        </td>
                        <td className="bg-slate-50 px-2.5 py-2.5 text-right font-bold text-slate-900">
                          {fmtDkk(cv.totalLtvRev)}
                        </td>
                        <td className="bg-slate-50 px-2.5 py-2.5 text-right font-semibold text-slate-800">
                          {fmtDkk(cv.ltvPerLead)}
                        </td>
                        <td className="bg-slate-50 px-3 py-2.5 text-right">
                          {cv.totalLtvRoasPct != null ? (
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${roasBadgeClass(
                                cv.totalLtvRoasPct
                              )}`}
                            >
                              {fmtPct(cv.totalLtvRoasPct)}
                            </span>
                          ) : (
                            <span className="text-slate-400">–</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-900 font-bold text-white">
                  <tr>
                    <td className="sticky left-0 z-10 bg-slate-900 px-3 py-3">
                      TOTAL ({filteredCohorts.length} MÅNEDER)
                    </td>
                    <td className="px-2.5 py-3 text-right">{fmtInt(activeSummary.unique_leads)}</td>
                    <td className="px-2.5 py-3 text-right">{fmtDkk(activeSummary.spend_dkk)}</td>
                    <td className="px-2.5 py-3 text-right">{fmtDkk(activeSummary.cpl_dkk)}</td>
                    <td className="px-2.5 py-3 text-right text-amber-300">
                      {fmtDkk(summaryView.immRev)}
                    </td>
                    <td className="px-2.5 py-3 text-right text-amber-300">
                      {fmtPct(summaryView.immRoasPct)}
                    </td>
                    <td className="px-2.5 py-3 text-right text-emerald-300">
                      +{fmtInt(summaryView.m0EmailRev)}
                    </td>
                    <td className="px-2.5 py-3 text-right text-emerald-300" colSpan={6}>
                      Efterfølgende måneder (M+1..M+24): +{fmtDkk(summaryView.afterEmailRev)}
                    </td>
                    <td className="px-2.5 py-3 text-right text-emerald-300">
                      +{fmtDkk(summaryView.emailRev)} (+{fmtPct(summaryView.emailRoasLiftPct)})
                    </td>
                    <td className="px-2.5 py-3 text-right text-white">
                      {fmtDkk(summaryView.totalLtvRev)}
                    </td>
                    <td className="px-2.5 py-3 text-right text-emerald-300">
                      {fmtDkk(summaryView.ltvPerLead)}
                    </td>
                    <td className="px-3 py-3 text-right text-emerald-300">
                      {fmtPct(summaryView.totalLtvRoasPct)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* ─── SECTION 4: Country Comparison + Methodology Note ─── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Country Comparison Table */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-7">
              <div className="flex items-center gap-2">
                <BarChart3 size={18} className="text-[#0F3F34]" />
                <h3 className="text-base font-bold text-slate-900">
                  Norge vs. Sverige — 24 Mdr. LTV Benchmark (Annonce-kohorter)
                </h3>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Direkte sammenligning af Immediate ROAS vs. Email LTV-løft på tværs af Sverige og Norge (følger valgt attribuerings-model ovenfor).
              </p>

              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-bold uppercase">Marked</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">Leads</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">Ad Spend</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">CPL</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">Imm. ROAS</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">Email Løft</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">Total LTV ROAS</th>
                      <th className="px-3 py-2.5 text-right font-bold uppercase">LTV / Lead</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {(
                      [
                        { id: 'se' as LtvCountry, name: '🇸🇪 Sverige (SE)', stat: comparison.se },
                        { id: 'no' as LtvCountry, name: '🇳🇴 Norge (NO)', stat: comparison.no },
                        { id: 'all' as LtvCountry, name: '🌍 Samlet (NO + SE)', stat: comparison.all },
                      ] as const
                    ).map((row) => {
                      const rv = getSummaryView(row.stat, attributionMode);
                      return (
                        <tr
                          key={row.id}
                          onClick={() => handleCountryChange(row.id)}
                          className={`cursor-pointer ${
                            country === row.id ? 'bg-emerald-50/60 font-semibold' : 'hover:bg-slate-50'
                          }`}
                        >
                          <td className="px-3 py-3 font-bold text-slate-900">{row.name}</td>
                          <td className="px-3 py-3 text-right">{fmtInt(row.stat.unique_leads)}</td>
                          <td className="px-3 py-3 text-right">{fmtDkk(row.stat.spend_dkk)}</td>
                          <td className="px-3 py-3 text-right">{fmtDkk(row.stat.cpl_dkk, true)}</td>
                          <td className="px-3 py-3 text-right font-semibold text-amber-800">
                            {fmtPct(rv.immRoasPct)} ({fmtDkk(rv.immRev)})
                          </td>
                          <td className="px-3 py-3 text-right font-bold text-emerald-700">
                            +{fmtPct(rv.emailRoasLiftPct)} (+{fmtDkk(rv.emailRev)})
                          </td>
                          <td className="px-3 py-3 text-right">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${roasBadgeClass(
                                rv.totalLtvRoasPct
                              )}`}
                            >
                              {fmtPct(rv.totalLtvRoasPct)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right font-bold text-slate-900">
                            {fmtDkk(rv.ltvPerLead, true)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Methodology & CoReg Note */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-5">
              <div className="flex items-center gap-2">
                <Info size={18} className="text-[#0F3F34]" />
                <h3 className="text-base font-bold text-slate-900">
                  Sådan er LTV & Email-attribuering beregnet
                </h3>
              </div>
              <ul className="mt-3 space-y-2.5 text-xs text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>
                    <strong>Person-niveau Kohorte (Første Lead-dato):</strong> Hvert unikt lead (email) placeres i den
                    måned, personen første gang udfyldte låneberegneren på SimplyLoan. Hvis samme person senere klikker i
                    en email og udfylder beregneren igen (nyt LeadID), føres salget stadig tilbage til personens{' '}
                    <em>oprindelige</em> kohorte-måned.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>
                    <strong>3 Attribuerings-modeller (Toggle i toppen):</strong> Skift frit mellem{' '}
                    <em>Kun 1:1 LeadID</em> (strengeste match), <em>Standard Kohorte</em> (inkl. direkte tilbudsvæg i
                    annoncemåneden) og <em>Inkl. Fordelt Skjult LTV</em> (hvor unmatchet email fra <code>s2="email"</code>,{' '}
                    <code>fbem</code>, <code>brem</code>, <code>slsebrlist</code> samt 0-spend return-web fordeles
                    proportionalt på de betalte kohorter).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>
                    <strong>Kun Ægte SimplyLoan Leads i Kohorte-ROAS:</strong> Udover native SimplyLoan leads er der i
                    Brevo også synkroniseret CoReg-leads (markeret med <code>c_</code>-ID). Disse har genereret yderligere{' '}
                    <strong>{fmtDkk(payload.email_channel_breakdown.coreg_imported_email_dkk)}</strong> i email-omsætning,
                    men holdes adskilt her, så dine annonce-kohorters LTV ROAS er 100% ren.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SimplyloanLtvDashboard;
